import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import Button from '../../../components/form/button';
import Input from '../../../components/form/input';
import Modal from '../../../components/modal/default';
import { isValidPassword } from '../../../components/password-hints';
import useAuth from '../../../hooks/auth';
import useRouter from '../../../hooks/router';
import { logger } from '../../../utils/logger';
import styles from './styles.module.css';

const UsersResetView: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tried, setTried] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>();
  const [pass, setPass] = useState('');
  const { logout, reset, isLoading } = useAuth();
  const { query } = useRouter();
  const token = query.has('token') ? query.get('token') : undefined;

  // clear out any current auth state, they are trying to reset
  useEffect(() => logout(), []);

  useEffect(() => {
    // if they've come here without a token, a no go
    if (!token) return setIsOpen(true);

    // if reset returned an error
    if (!isLoading && tried && !success) return setIsOpen(true);

    // upon success, show result
    if (!isLoading && success) return setIsOpen(true);
  }, [token, isLoading, tried, success]);

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleSetPass = (e: ChangeEvent<HTMLInputElement>) => {
    setPass(e.target.value);
  };

  const handleReset = async () => {
    try {
      setTried(true);
      await reset({ token: token as string, pass });
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message);
      err.message = `Reset error: ${err?.message}`;
      logger.error(err);
    }
  };

  const getModalTitle = (): string => {
    // if they've come here without a token, a no go
    if (!token) return 'Invalid token';

    // if reset returned an error
    if (tried && !success) return 'Server Error';

    // upon success
    if (tried && success) return 'Success';

    return '';
  };

  const getModalContent = () => {
    // if they've come here without a token, a no go
    if (!token)
      return (
        <div className={styles.notifyContainer}>
          <div className={styles.notifyIcon}>
            <FontAwesomeIcon icon={faExclamationTriangle as IconProp} color="orange" />
          </div>
          <div className={styles.notifyText}>
            Problem with the unique token in the URL. Please make sure you click on the link in your
            email or copy and paste the entire URL into your browser.
          </div>
        </div>
      );

    // if reset returned an error
    if (tried && !success)
      return (
        <div className={styles.notifyContainer}>
          <div className={styles.notifyIcon}>
            <FontAwesomeIcon icon={faExclamationCircle as IconProp} color="red" />
          </div>
          <div className={styles.notifyText}>
            There was an error processing your request: <br />
            <br />
            {error}
          </div>
        </div>
      );

    return (
      <div className={styles.notifyContainer}>
        <div className={styles.notifyIcon}>
          <FontAwesomeIcon icon={faCheckCircle as IconProp} color="green" />
        </div>
        <div className={styles.notifyText}>
          Password set successfully. You may close this window and log into all Limbic services with
          your new password.
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <Input.Password
        className={styles.input}
        placeholder="Password"
        value={pass}
        onChange={handleSetPass}
        disabled={isLoading}
      />

      <div className="buttons">
        <Button
          variant="dark"
          type="submit"
          onClick={handleReset}
          disabled={!isValidPassword(pass) || isLoading}
          isLoading={isLoading}
          dot="green">
          Set New Password
        </Button>
      </div>

      <Modal
        isOpen={isOpen}
        closeOnEsc={!(tried && success)}
        closeOnOverlayClick={!(tried && success)}
        onClose={handleCloseModal}
        title={getModalTitle()}>
        {getModalContent()}
      </Modal>
    </div>
  );
};

export default UsersResetView;
